import React, { useContext, useRef, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useTranslation } from 'react-i18next';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell, MultiValueEvaluationCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { ExtendedCompleteRiskFieldsEvaluationHistory } from '@pages-clean/risk-assessment/risk-log/constants';
import AddEditEvaluationDialog from '@pages-clean/risk-assessment/risk-log/dialogs/evaluation/evaluation-dialog';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';

interface EvaluationChartProps {
    evaluations: ExtendedCompleteRiskFieldsEvaluationHistory[];
}

const EvaluationsGrid = ({ evaluations }: EvaluationChartProps) => {
    const user = useAuthorization();
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });
    const { TooltipCell } = useGridCustomCell();
    const { current } = useContext(RiskLogContext);
    const { study } = current;
    const risk = current.selectionState.selectedItem;
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const exportFileName = `${risk?.statement?.substring(0, 80)}-${t('grids.evaluations.exportFileName')}`;
    const newestEvaluation = evaluations[0];
    const showAddEvaluationDialog = useBoolean(false);
    const addEvaluationAction = useRef(false);
    const evaluationsModified = useRef(false);
    const [selectedEvaluation, setSelectedEvaluation] = useState<ExtendedCompleteRiskFieldsEvaluationHistory>();

    const [filterState, setFilterState] = useState<CompositeFilterDescriptor | undefined>({
        logic: 'and',
        filters: [],
    });

    const onEvaluationDialogClose = () => {
        showAddEvaluationDialog.setFalse();
    };

    const onSaveEvaluationDialog = () => {
        setFilterState({ logic: 'and', filters: [] });
        evaluationsModified.current = true;
        showAddEvaluationDialog.setFalse();
    };

    const columns: GridColumnProps[] = [
        {
            id: 'score',
            field: 'score',
            title: t('grids.evaluations.columns.score'),
            minWidth: 190,
            ragColumn: true,
            evaluationColumn: true,
            filterKey: 'score',
            filter: 'numeric',
            cells: {
                data: (gridCellProps) =>
                    MultiValueEvaluationCell({
                        ...gridCellProps,
                        highlighted: true,
                        showBlankText: false,
                    }),
            },
            multiValueExportTitles: {
                prevScore: t('grids.evaluations.exportColumns.prevScore'),
                score: t('grids.evaluations.exportColumns.score'),
                movement: t('grids.evaluations.exportColumns.movement'),
            },
        },
        {
            id: 'likelihood',
            field: 'likelihood',
            title: t('grids.evaluations.columns.likelihood'),
            minWidth: 120,
            filter: 'numeric',
        },
        {
            id: 'impact',
            field: 'impact',
            title: t('grids.evaluations.columns.impact'),
            minWidth: 120,
            filter: 'numeric',
        },
        {
            id: 'detectability',
            field: 'detectability',
            title: t('grids.evaluations.columns.detectability'),
            minWidth: 120,
            filter: 'numeric',
        },
        {
            id: 'rationale',
            field: 'rationale',
            title: t('grids.evaluations.columns.rationale'),
            minWidth: 250,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'changeReason',
            field: 'changeReason',
            title: t('grids.evaluations.columns.justificationForChange'),
            minWidth: 300,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'evaluationModel',
            field: 'evaluationModel.name',
            title: t('grids.evaluations.columns.evaluationModel'),
            minWidth: 300,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'createdBy',
            field: 'createdBy',
            title: t('grids.evaluations.columns.createdBy'),
            minWidth: 180,
        },
        {
            id: 'created',
            field: 'created',
            title: t('grids.evaluations.columns.created'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            id: 'updatedBy',
            field: 'updatedBy',
            title: t('grids.evaluations.columns.updatedBy'),
            minWidth: 180,
        },
        {
            id: 'updated',
            field: 'updated',
            title: t('grids.evaluations.columns.updated'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 150,
            cellName: 'DateCell',
        },
    ];

    return (
        <>
            <Grid
                excludeHiddenRowMenuActions
                title={t('grids.evaluations.title')}
                maxHeight="100%"
                testDataId="risklog-evaluations-grid"
                data={evaluations}
                columns={columns}
                filterable
                showAddIcon
                addRecordTooltip={t('grids.evaluations.addRecordLabel')}
                useLoadingOverlay
                canEdit={canEdit}
                defaultSelectedItem={evaluationsModified.current ? evaluations[0] : undefined}
                addCallback={() => {
                    evaluationsModified.current = false;
                    setSelectedEvaluation(undefined);
                    addEvaluationAction.current = true;
                    showAddEvaluationDialog.setTrue();
                }}
                scrollable="virtual"
                dataItemKey="id"
                exportFilename={`${exportFileName}_${study?.studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate(
                    'DD-MMM-YYYY',
                )}`}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                removeCardBorder
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('grids.evaluations.actions.edit'),
                        onClick: (dataItem) => {
                            evaluationsModified.current = false;
                            addEvaluationAction.current = false;
                            const evaluation = dataItem as ExtendedCompleteRiskFieldsEvaluationHistory;
                            setSelectedEvaluation(evaluation);
                            showAddEvaluationDialog.setTrue();
                        },
                        disabled: !canEdit,
                        icon: ['far', 'pen-to-square'],
                        showMenuItem: (dataItem) => {
                            const evaluation = dataItem as ExtendedCompleteRiskFieldsEvaluationHistory;
                            return evaluation && newestEvaluation && evaluation.id === newestEvaluation.id;
                        },
                    },
                ]}
                filter={filterState}
                persistentStorageKey={String(PersistentStorageKeys['risk-log-evaluations'])}
            />
            {showAddEvaluationDialog.value && study && (
                <AddEditEvaluationDialog
                    onClose={onEvaluationDialogClose}
                    onSubmit={onSaveEvaluationDialog}
                    study={study}
                    risk={current.selectionState.selectedItem!}
                    evaluation={
                        addEvaluationAction.current &&
                        evaluations[0] &&
                        evaluations[0].evaluationModelId === study.evaluationModelId
                            ? evaluations[0]
                            : selectedEvaluation
                    }
                    evaluationCount={evaluations.length}
                    addEvaluationAction={addEvaluationAction.current}
                />
            )}
        </>
    );
};

export default EvaluationsGrid;
